import { SigningRequest } from '@microsoft/esign.gateway.service.client';
import { Constants } from '../../../common/Constants';
import { ApprovalRequestType } from '../../../data/types.generated';
import { LogCategory } from '../../../logger';
import {
    ApprovalCreator,
    CreateAdaptiveCardClient,
    CreateAdaptiveCardControllerInput,
    DeleteAdaptiveCardClient,
    DeleteAdaptiveCardControllerInput,
    SignApprovalType,
} from '../../../service/gen';
import {
    CreateAdaptiveCardDocusignClient,
    CreateAdaptiveCardDocusignControllerInput,
    DeleteAdaptiveCardDocusignClient,
    DraftEnvelopeInput,
} from '../../../service/gen/esign';
import { mapDraftEnvelopeInputToSigningRequest } from '../../../utilities/MapESignModel';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { delay } from '../../../utilities/Utils';
import { createEsignApproval } from '../CreateEsignApproval/CreateEsignApproval';
import { retrieveDocusignRequestDefaults } from '../utils/Authentication/DocusignAuthHelpers';

export const createDocusignApproval = async (
    envelopeInput: DraftEnvelopeInput,
    setEnvelopeUrl: (value: string) => void,
    setLoadingPage: (value: boolean) => void,
    setLoadingPageMessage: (message: string) => void,
    setErrorPage: (value: boolean) => void,
    setErrorPageMessage: (value: string) => void
): Promise<void> => {
    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();

    if (setLoadingPage) {
        setLoadingPageMessage(loc.strings.approval_create_loading_text);
        setLoadingPage(true);
    }

    const docusignRequestDefaults = await retrieveDocusignRequestDefaults();
    envelopeInput.EsignToken = docusignRequestDefaults.token;
    envelopeInput.ApiEndpoint = docusignRequestDefaults.apiAccessPoint;

    logger.logTraceInfo(
        LogCategory.CreateEsignApprovalView,
        `Calling Esign Approvals create endpoint from ${envelopeInput.Creator}`
    );

    // If created from chat, send the adaptive card first to get the message id.
    if (
        envelopeInput.WebhookBaseDataObject != null &&
        envelopeInput.WebhookBaseDataObject.ApprovalCreator !== ApprovalCreator.PersonalApp
    ) {
        if (host.getEcsConfig().MicrosoftTeamApprovals.EsignTeamsEndpointMigrationEnabled) {
            const cardClient = new CreateAdaptiveCardClient();
            const adaptiveCardInput: CreateAdaptiveCardControllerInput = {
                SignApprovalType: SignApprovalType.Docusign,
                UserName: envelopeInput.UserName,
                UserEmail: host.getUserPrincipalName(),
                ClientRequestId: envelopeInput.ClientRequestId,
                WebhookBaseDataObject: envelopeInput.WebhookBaseDataObject,
            };
            envelopeInput.Creator = envelopeInput.WebhookBaseDataObject.ApprovalCreator;
            const adaptiveCardOutput = await cardClient.createAdaptiveCard(adaptiveCardInput);
            envelopeInput.WebhookBaseDataObject.MessageId = adaptiveCardOutput.MessageId;
        } else {
            const cardClient = new CreateAdaptiveCardDocusignClient();
            const adaptiveCardInput: CreateAdaptiveCardDocusignControllerInput = {
                EnvelopeData: {
                    ...envelopeInput,
                    UserEmail: host.getUserPrincipalName(),
                },
            };
            envelopeInput.Creator = envelopeInput.WebhookBaseDataObject.ApprovalCreator;
            const adaptiveCardOutput = await cardClient.createAdaptiveCard(adaptiveCardInput);
            envelopeInput.WebhookBaseDataObject.MessageId = adaptiveCardOutput.MessageId;
        }
    }

    try {
        const signingRequestUrl = await createEsignApproval(
            ApprovalRequestType.Docusign,
            (): SigningRequest => mapDraftEnvelopeInputToSigningRequest(envelopeInput)
        );
        setEnvelopeUrl(signingRequestUrl);
        await delay(Constants.screenReaderWaitTime);
        setLoadingPageMessage(loc.strings.approval_create_success_text);
    } catch (error: any) {
        if (envelopeInput.Creator !== ApprovalCreator.PersonalApp) {
            // Clean up the adaptive card, if any.
            if (envelopeInput.WebhookBaseDataObject.MessageId != null) {
                if (host.getEcsConfig().MicrosoftTeamApprovals.EsignTeamsEndpointMigrationEnabled) {
                    const cardClient = new DeleteAdaptiveCardClient();
                    const input: DeleteAdaptiveCardControllerInput = {
                        SignApprovalType: SignApprovalType.Docusign,
                        WebhookBaseDataObject: envelopeInput.WebhookBaseDataObject,
                    };
                    cardClient.deleteAdaptiveCard(input);
                } else {
                    const cardClient = new DeleteAdaptiveCardDocusignClient();
                    cardClient.deleteAdaptiveCard(envelopeInput.WebhookBaseDataObject);
                }
            }
        }

        const errorCode = error?.message as string;

        if (setErrorPage) {
            setErrorPage(true);
            if (errorCode?.includes('unable_to_convert_document')) {
                setErrorPageMessage(loc.strings.docusign_upload_error_conversion_details);
            }
            const userErrorMessage = loc.strings.error_default;
            setErrorPageMessage(userErrorMessage);
        }
    } finally {
        if (setLoadingPage) {
            setLoadingPage(false);
        }
    }
};
